/** Pool of stateless QR decode workers. */
export type Decoder = 'zxing' | 'rqrr';

interface PendingJob {
  resolve: (symbols: Uint8Array[]) => void;
  reject: (error: Error) => void;
}

interface WorkerReply {
  type: 'decoded' | 'error';
  jobId: number;
  count?: number;
  symbols?: ArrayBuffer[];
  message?: string;
}

export class QrDecodePool {
  private readonly workers: Worker[] = [];
  private readonly pending = new Map<number, PendingJob>();
  private nextWorker = 0;
  private nextJobId = 1;
  private terminated = false;

  constructor(size: number) {
    const count = Math.max(1, Math.floor(size));
    for (let i = 0; i < count; i++) {
      const worker = new Worker(new URL('./decode.worker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (event: MessageEvent<WorkerReply>) => this.onMessage(event.data);
      worker.onerror = (event) => this.failAll(new Error(event.message || 'decode worker failed'));
      this.workers.push(worker);
    }
  }

  get size(): number {
    return this.workers.length;
  }

  decode(
    imageData: ImageData,
    decoder: Decoder,
    maxSymbols: number,
  ): Promise<Uint8Array[]> {
    if (this.terminated) return Promise.reject(new Error('decode pool terminated'));
    const worker = this.workers[this.nextWorker];
    if (!worker) return Promise.reject(new Error('decode pool has no worker'));
    this.nextWorker = (this.nextWorker + 1) % this.workers.length;

    const source = imageData.data;
    const buffer = source.buffer.slice(source.byteOffset, source.byteOffset + source.byteLength) as ArrayBuffer;
    const jobId = this.nextJobId++;

    return new Promise<Uint8Array[]>((resolve, reject) => {
      this.pending.set(jobId, { resolve, reject });
      worker.postMessage(
        {
          type: 'frame',
          buffer,
          width: imageData.width,
          height: imageData.height,
          decoder,
          maxSymbols,
          jobId,
        },
        [buffer],
      );
    });
  }

  terminate(): void {
    if (this.terminated) return;
    this.terminated = true;
    for (const worker of this.workers) worker.terminate();
    this.workers.length = 0;
    this.failAll(new Error('decode pool terminated'));
  }

  private onMessage(msg: WorkerReply): void {
    const job = this.pending.get(msg.jobId);
    if (!job) return;
    this.pending.delete(msg.jobId);
    if (msg.type === 'error' || !msg.symbols) {
      job.reject(new Error(msg.message || 'decode failed'));
      return;
    }
    job.resolve(msg.symbols.map((buffer) => new Uint8Array(buffer)));
  }

  private failAll(error: Error): void {
    for (const job of this.pending.values()) job.reject(error);
    this.pending.clear();
  }
}
